import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useIsAuthenticated } from '@azure/msal-react';
import { createPortal } from 'react-dom';

import { startup, startupFinished } from './actions';
import SessionRouter from './components/navigation/SessionRouter';
import PublicRouter from './components/navigation/PublicRouter';
import CustomModal from './components/common/CustomModal';
import { ImpersonateSession } from './components/common/ImpersonateSession';

import { useOneIncScript } from './hooks/useOneIncScript';
import { useB2CInfo } from './hooks';
import { doNothing } from './services/utils';
import app from './app.json';
import FigoLoadingOverlay from './components/common/FigoLoadingOverlay';

const VersionData = () => createPortal(
  <meta data-version={app.version} name="App version" />,
  document.head,
);

const AppRouter = () => {
  const dispatch = useDispatch();
  useOneIncScript();

  const pathname = window.location.pathname.split('/')[1];
  const store = useSelector(({
    policies,
    session,
  }) => ({ policies, session }), shallowEqual);
  const { logoutB2C, getAllTokens } = useB2CInfo();
  const [allTokensLoaded, setAllTokensLoaded] = useState(false);
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    switch (pathname) {
      case 'login':
        logoutB2C();
        break;
      case 'paymentMethod':
        localStorage.clear();
        dispatch(startupFinished());
        break;
      default:
        doNothing();
    }

    return () => null;
  }, []);

  const getTokensBeforeStartUp = async () => {
    await getAllTokens();
    dispatch(startup());
    setAllTokensLoaded(true);
  };

  const hasSession = useMemo(() => (
    Boolean(isAuthenticated
      && store.session.privateToken
      && store.session.userInfo)
  ), [isAuthenticated, store.session.privateToken, store.session.userInfo]);

  useEffect(() => {
    if (isAuthenticated) {
      getTokensBeforeStartUp();
    }
  }, [isAuthenticated]);

  const renderPageContent = () => {
    if (isAuthenticated) {
      if (hasSession && allTokensLoaded) {
        return <SessionRouter />;
      }

      // waiting for session
      return <FigoLoadingOverlay visible />;
    }

    return <PublicRouter />;
  };

  return (
    <Router>
      {renderPageContent()}

      <CustomModal />

      <ImpersonateSession />

      <VersionData />
    </Router>
  );
};

export default AppRouter;
